import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { mainAppUrl } from '../../config/adminHost';
import { adminCompanyPath, adminUserPath } from '../../constants/adminPaths';
import { useAdminAuth } from '../../context/AdminAuthContext';
import {
  cancelSpecialAccessInvite,
  createSpecialAccessInvite,
  listSpecialAccessInvites,
  resendSpecialAccessInvite,
} from '../services/specialAccessInvitationsApi';
import { PageSkeleton } from '../../../components/loading/skeletons';
import './SpecialAccessInvitesSection.css';

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

function specialAccessAcceptUrl(token) {
  return mainAppUrl(`/app/special-access/accept?token=${encodeURIComponent(token)}`);
}

function buildFallbackInvite({ token, email, admin }) {
  const now = new Date();
  const expiresAt = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

  return {
    token,
    email,
    status: 'pending',
    invitedByEmail: admin?.email || null,
    invitedByName: `${admin?.firstName || ''} ${admin?.lastName || ''}`.trim() || null,
    createdAt: now.toISOString(),
    expiresAt: expiresAt.toISOString(),
    acceptedAt: null,
    acceptedBy: null,
    acceptedCompanyId: null,
    acceptedUserName: '',
    companyName: '',
  };
}

export default function SpecialAccessInvitesSection({ showHeader = true }) {
  const navigate = useNavigate();
  const { admin } = useAdminAuth();
  const [activeTab, setActiveTab] = useState('pending');
  const [email, setEmail] = useState('');
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingCancelToken, setLoadingCancelToken] = useState('');
  const [loadingResendToken, setLoadingResendToken] = useState('');
  const [pendingItems, setPendingItems] = useState([]);
  const [acceptedItems, setAcceptedItems] = useState([]);
  const [expiredItems, setExpiredItems] = useState([]);
  const [loadedTabs, setLoadedTabs] = useState({ pending: false, accepted: false, expired: false });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadTab = async (status) => {
    const invites = await listSpecialAccessInvites({ admin, status });
    if (status === 'pending') {
      setPendingItems(invites);
    } else if (status === 'accepted') {
      setAcceptedItems(invites);
    } else {
      setExpiredItems(invites);
    }
    setLoadedTabs((prev) => ({ ...prev, [status]: true }));
    return invites;
  };

  const load = async ({ statuses = ['pending', 'accepted', 'expired'] } = {}) => {
    try {
      setLoadingList(true);
      setError('');
      await Promise.all(statuses.map((status) => loadTab(status)));
    } catch (err) {
      setError(err?.message || 'Failed to load invites');
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (admin?.uid) {
      load({ statuses: ['pending'] }).then(() => {
        void Promise.all([loadTab('accepted'), loadTab('expired')]);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin?.uid]);

  useEffect(() => {
    if (!admin?.uid || loadedTabs[activeTab]) {
      return;
    }
    void loadTab(activeTab).catch((err) => {
      setError(err?.message || 'Failed to load invites');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, admin?.uid, loadedTabs]);

  const handleCreate = async () => {
    const normalizedEmail = String(email || '').trim();
    if (!isValidEmail(normalizedEmail)) {
      setError('Please enter a valid email address.');
      setSuccess('');
      return;
    }

    try {
      setLoadingCreate(true);
      setError('');
      setSuccess('');
      const response = await createSpecialAccessInvite({ email: normalizedEmail, admin });
      const invite = response.invite || buildFallbackInvite({
        token: response.token,
        email: normalizedEmail,
        admin,
      });

      setPendingItems((prev) => [invite, ...prev.filter((item) => item.token !== invite.token)]);
      setLoadedTabs((prev) => ({ ...prev, pending: true }));
      setSuccess(`Invite sent to ${normalizedEmail}`);
      setEmail('');

      try {
        await navigator.clipboard.writeText(specialAccessAcceptUrl(response.token));
      } catch {
        // Ignore clipboard failures.
      }
    } catch (err) {
      setError(err?.message || 'Failed to create invite');
    } finally {
      setLoadingCreate(false);
    }
  };

  const handleResend = async (token) => {
    try {
      setLoadingResendToken(token);
      setError('');
      setSuccess('');
      await resendSpecialAccessInvite({ token, admin });
      setSuccess('Invite resent');
    } catch (err) {
      setError(err?.message || 'Failed to resend invite');
    } finally {
      setLoadingResendToken('');
    }
  };

  const handleCancel = async (token) => {
    const fromExpired = expiredItems.some((invite) => invite.token === token);
    const confirmMessage = fromExpired
      ? 'Remove this expired invite?'
      : 'Cancel this special access invite?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    const previousPending = pendingItems;
    const previousExpired = expiredItems;
    setPendingItems((prev) => prev.filter((invite) => invite.token !== token));
    setExpiredItems((prev) => prev.filter((invite) => invite.token !== token));

    try {
      setLoadingCancelToken(token);
      setError('');
      setSuccess('');
      await cancelSpecialAccessInvite({ token, admin });
      setSuccess(fromExpired ? 'Expired invite removed' : 'Invite cancelled');
    } catch (err) {
      setPendingItems(previousPending);
      setExpiredItems(previousExpired);
      setError(err?.message || 'Failed to cancel invite');
    } finally {
      setLoadingCancelToken('');
    }
  };

  const handleCopyLink = async (token) => {
    try {
      await navigator.clipboard.writeText(specialAccessAcceptUrl(token));
      setSuccess('Invite link copied');
    } catch {
      setError('Failed to copy invite link');
    }
  };

  return (
    <div className="special-access-section">
      {showHeader && (
        <>
          <h2 className="special-access-header-title">Special Access Invites</h2>
          <p className="special-access-header-subtitle">
            Invite someone by email so they can register their own company owner account.
          </p>
        </>
      )}

      {error && (
        <div className="special-access-alert special-access-alert-error">
          <span>!</span>
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="special-access-alert special-access-alert-success">
          <span>✓</span>
          <span>{success}</span>
        </div>
      )}

      <div className="special-access-card">
        <h3 className="special-access-card-title">Create Invite</h3>
        <div className="special-access-form-row">
          <div className="special-access-form-group">
            <label className="special-access-label">Email</label>
            <input
              type="email"
              className="special-access-input"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) {
                  setError('');
                }
              }}
              placeholder="owner@example.com"
              disabled={loadingCreate}
            />
          </div>
          <button
            className="special-access-btn special-access-btn-primary"
            onClick={handleCreate}
            disabled={loadingCreate || !email.trim()}
          >
            {loadingCreate ? 'Sending...' : 'Send Invite'}
          </button>
        </div>
      </div>

      <div className="special-access-card">
        <div className="special-access-card-header">
          <h3 className="special-access-card-title">Invite Records</h3>
          <button
            className="special-access-refresh-btn"
            onClick={() => load({ statuses: [activeTab] })}
            disabled={loadingList}
            title="Refresh"
          >
            ⟳
          </button>
        </div>
        <div className="special-access-tabs" role="tablist" aria-label="Special access invite tabs">
          <button
            className={`special-access-tab ${activeTab === 'pending' ? 'special-access-tab-active' : ''}`}
            onClick={() => setActiveTab('pending')}
            type="button"
          >
            Pending ({pendingItems.length})
          </button>
          <button
            className={`special-access-tab ${activeTab === 'accepted' ? 'special-access-tab-active' : ''}`}
            onClick={() => setActiveTab('accepted')}
            type="button"
          >
            Accepted ({acceptedItems.length})
          </button>
          <button
            className={`special-access-tab ${activeTab === 'expired' ? 'special-access-tab-active' : ''}`}
            onClick={() => setActiveTab('expired')}
            type="button"
          >
            Expired ({expiredItems.length})
          </button>
        </div>
        <div className="special-access-divider"></div>

        {activeTab === 'pending' && (pendingItems.length === 0 ? (
          loadingList ? (
            <PageSkeleton showHeader={false} rows={4} label="Loading invites" />
          ) : (
            <p className="special-access-empty">No pending invites.</p>
          )
        ) : (
          <div className="special-access-list">
            {pendingItems.map((invite) => (
              <div key={invite.token} className="special-access-item">
                <div className="special-access-item-content">
                  <div className="special-access-item-email">{invite.email}</div>
                </div>
                <span className="special-access-badge">Special Access</span>
                <div className="special-access-item-actions">
                  <button
                    className="special-access-action-btn"
                    onClick={() => handleCopyLink(invite.token)}
                    title="Copy invite link"
                    disabled={loadingCancelToken === invite.token || loadingResendToken === invite.token}
                  >
                    📋
                  </button>
                  <button
                    className="special-access-action-btn"
                    onClick={() => handleResend(invite.token)}
                    title="Resend invite"
                    disabled={loadingResendToken === invite.token || loadingCancelToken === invite.token}
                  >
                    {loadingResendToken === invite.token ? '…' : '✉️'}
                  </button>
                  <button
                    className="special-access-action-btn special-access-action-btn-danger"
                    onClick={() => handleCancel(invite.token)}
                    title="Cancel invite"
                    disabled={loadingCancelToken === invite.token || loadingResendToken === invite.token}
                  >
                    {loadingCancelToken === invite.token ? '…' : '✕'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ))}

        {activeTab === 'accepted' && (acceptedItems.length === 0 ? (
          loadingList ? (
            <PageSkeleton showHeader={false} rows={4} label="Loading invites" />
          ) : (
            <p className="special-access-empty">No accepted accounts yet.</p>
          )
        ) : (
          <div className="special-access-list">
            {acceptedItems.map((invite) => (
              <div key={invite.token} className="special-access-item">
                <div className="special-access-item-content">
                  <div className="special-access-item-email">{invite.email}</div>
                  <div className="special-access-item-meta">
                    {[invite.companyName, invite.acceptedUserName, invite.acceptedAt ? `Accepted ${new Date(invite.acceptedAt).toLocaleDateString()}` : '']
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                </div>
                <span className="special-access-badge">Accepted</span>
                <div className="special-access-item-actions">
                  {invite.acceptedBy && (
                    <button
                      className="special-access-action-btn"
                      onClick={() => navigate(adminUserPath(invite.acceptedBy))}
                      title="Open user profile"
                    >
                      👤
                    </button>
                  )}
                  {invite.acceptedCompanyId && (
                    <button
                      className="special-access-action-btn"
                      onClick={() => navigate(adminCompanyPath(invite.acceptedCompanyId))}
                      title="Open company profile"
                    >
                      🏢
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}

        {activeTab === 'expired' && (expiredItems.length === 0 ? (
          loadingList ? (
            <PageSkeleton showHeader={false} rows={4} label="Loading invites" />
          ) : (
            <p className="special-access-empty">No expired invites.</p>
          )
        ) : (
          <div className="special-access-list">
            {expiredItems.map((invite) => (
              <div key={invite.token} className="special-access-item">
                <div className="special-access-item-content">
                  <div className="special-access-item-email">{invite.email}</div>
                  <div className="special-access-item-meta">
                    {invite.expiresAt ? `Expired ${new Date(invite.expiresAt).toLocaleDateString()}` : 'Expired'}
                  </div>
                </div>
                <span className="special-access-badge">Expired</span>
                <div className="special-access-item-actions">
                  <button
                    className="special-access-action-btn special-access-action-btn-danger"
                    onClick={() => handleCancel(invite.token)}
                    title="Remove expired invite"
                    disabled={loadingCancelToken === invite.token}
                  >
                    {loadingCancelToken === invite.token ? '…' : '✕'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
